<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="exercise-definition-dialog-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form
        ref="form"
        class="exercise-definition-dialog-form"
        @submit.prevent="onSubmit"
      >
        <div class="exercise-definition-dialog-form-body">
          <v-toolbar
            flat
            height="76"
            class="exercise-definition-dialog__toolbar"
          >
            <v-toolbar-title class="exercise-definition-dialog-toolbar-title"
              >Select alternative exercises</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <div class="exercise-definition-dialog__selected">
            <v-container
              v-if="selectedExerciseDefinitionsData.length > 0"
              class="exercise-definition-dialog-card-container"
            >
              <h5>
                Selected exercises
                <small>(max. {{ maxSelectedExercises }})</small>
              </h5>
              <div class="main-user-table-wrapper">
                <table
                  class="elevation-0 main-user-table"
                  style="border-spacing:0 8px;"
                >
                  <draggable
                    group="exercises"
                    v-model="selectedExerciseDefinitionsData"
                    tag="tbody"
                  >
                    <exercise-definition-selectable-table-row
                      v-for="exercise in selectedExerciseDefinitionsData"
                      :key="exercise.id"
                      :user="exercise"
                      @click.native="chooseExerciseDefinition(exercise.id)"
                    />
                  </draggable>
                  <tr class="main-user-table-empty">
                    <td class="main-user-table-empty">&nbsp;</td>
                  </tr>
                </table>
              </div>
            </v-container>
            <v-container
              class="exercise-definition-dialog-card-container"
              v-if="addAlternatives"
            >
              <h5>Available exercises</h5>
              <v-row>
                <v-col sm="12" class="pb-0">
                  <v-text-field
                    :value="defaultQueryParams.query"
                    class="bh-input-pill rounded-pill"
                    color="accent"
                    height="48px"
                    placeholder="Search for exercises"
                    filled
                    clearable
                    hide-details
                    @input="onSearch('query', $event)"
                    @click:clear="clearSearch"
                    @click:append="runSearch"
                    @keydown.enter="runSearch"
                  >
                    <template v-slot:append>
                      <v-progress-circular
                        v-if="exerciseDefinitionsLoading"
                        size="16"
                        width="2"
                        color="info"
                        indeterminate
                      ></v-progress-circular>
                      <v-icon v-else>bhi-loupe</v-icon>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col sm="12" class="">
                  <main-user-table
                    :headers="headers"
                    :items="searchedExerciseDefinitions"
                    :items-per-page="queryParams.pageSize"
                    :server-items-length="pagination.totalCount || -1"
                    :page="queryParams.page"
                    :row-component="'exercise-definition-selectable-table-row'"
                    @choose-exercise="chooseExerciseDefinition"
                    @action:get-data="getSingleExercisesAction"
                  />
                </v-col>
              </v-row>
            </v-container>
          </div>
          <v-container
            class="d-flex justify-end align-center exercise-definition-dialog__tab--footer"
          >
            <v-btn rounded color="primary" elevation="0" @click="onSubmit">
              Select
            </v-btn>
          </v-container>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import MainUserTable from '@/components/tables/MainUserTable'
import RouterMixin from '@/mixins/router'
import { debounce } from '@/helpers/debounce'
import ExerciseDefinitionSelectableTableRow from '@/components/exercises/ExerciseDefinitionSelectableTableRow'
import draggable from 'vuedraggable'

const emptyForm = {}

export default {
  name: 'AlternativeExercisesDialog',
  components: {
    MainUserTable,
    ExerciseDefinitionSelectableTableRow,
    draggable,
  },
  mixins: [RouterMixin],
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    exerciseId: {
      type: [Number, null],
      default: null,
    },
    exerciseDefinitions: {
      type: Array,
      default: () => [],
    },
    alternativeExercises: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      default: () => {},
    },
    exerciseDefinitionsLoading: {
      type: Boolean,
      default: false,
    },
    addAlternatives: {
      type: Boolean,
      default: false,
    },
    participantsExercises: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      headers: [],
      searchedExerciseDefinitions: [],
      selectedExerciseDefinitionsData: [],
      maxSelectedExercises: 3,
      defaultQueryParams: {
        query: '',
        pageSize: 50,
      },
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true
        this.getSingleExercisesAction({ type: 'EXERCISE', folder: 'All' })
      } else {
        this.dialog = false
      }
    },
    exerciseDefinitions: function(val) {
      if (val) {
        this.selectedExerciseDefinitionsData = this.alternativeExercises.map(
          item => {
            return { ...item, ...{ selected: true } }
          },
        )
        this.calculateExerciseDefinitions()
      }
    },
  },
  mounted() {},
  computed: {},
  methods: {
    ...mapActions('exercisesStates', ['getDialogExerciseDefinitions']),
    closeDialog() {
      this.$emit('close-select-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    onInput({ name, value }) {
      this.newExerciseData[name] = value
      this.formIsValid()
    },
    onSubmit() {
      this.$emit('on-select', this.selectedExerciseDefinitionsData)
      this.closeDialog()
    },
    onSearch(key, value) {
      this.defaultQueryParams.query = value
      debounce(
        () => {
          this.getDataAction({ ...this.queryParams, query: value }, false)
        },
        1000,
        'ExercisesSearch',
      )
    },
    clearSearch() {
      this.defaultQueryParams.query = ''
      this.getDataAction({ ...this.queryParams }, false)
    },
    runSearch() {
      if (this.defaultQueryParams.query)
        this.onSearch('query', this.defaultQueryParams.query)
    },
    chooseExerciseDefinition(id) {
      let ind = this.selectedExerciseDefinitionsData.findIndex(
        exercise => exercise.id == id,
      )
      if (ind > -1) {
        if (this.participantsExercises) {
          this.selectedExerciseDefinitionsData[ind].selected = !this
            .selectedExerciseDefinitionsData[ind].selected
        } else {
          this.selectedExerciseDefinitionsData.splice(ind, 1)
        }
      } else if (
        this.selectedExerciseDefinitionsData.length < this.maxSelectedExercises
      ) {
        let tmpExercise = this.searchedExerciseDefinitions.find(
          exercise => exercise.id == id,
        )
        if (tmpExercise) tmpExercise.selected = true
        this.selectedExerciseDefinitionsData.push(tmpExercise)
      }
      this.calculateExerciseDefinitions()
    },
    calculateExerciseDefinitions() {
      this.searchedExerciseDefinitions = [...this.exerciseDefinitions]
      let ind = this.searchedExerciseDefinitions.findIndex(
        item => item.id == this.exerciseId,
      )
      if (ind > -1) this.searchedExerciseDefinitions.splice(ind, 1)
      this.searchedExerciseDefinitions.forEach((item, index) => {
        if (
          this.selectedExerciseDefinitionsData.findIndex(
            item2 => item2.id == item.id,
          ) > -1
        ) {
          this.searchedExerciseDefinitions[index] = Object.assign(
            {},
            this.searchedExerciseDefinitions[index],
            { selected: true },
          )
        } else this.searchedExerciseDefinitions[index]['selected'] = false
      })
    },
    getSingleExercisesAction(data) {
      debounce(
        () => {
          data.type = 'EXERCISE'
          data.folder = 'All'
          this.getDataAction({ ...this.queryParams, ...data }, false)
        },
        500,
        'ExercisesSearch',
      )
    },
    getData(data = {}) {
      if (this.dialog) this.getDialogExerciseDefinitions({ params: data })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.exercise-definition-dialog {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }
  &__toolbar {
    flex-grow: 0;
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1 * $gutter;
      padding-right: 1 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-file-button {
    margin-right: 10px;
    .v-btn {
      cursor: pointer;
    }
    & > label {
      cursor: pointer;
    }
    label {
    }
    .v-btn::before {
      display: none;
    }
    .v-icon {
      &:first-child {
        padding-right: 10px;
      }
      &:last-child {
        padding-left: 10px;
      }
    }
  }
  &-file-details {
    display: inline-block;
    vertical-align: middle;
    flex-grow: 1;
    img {
      width: 40px;
      height: 40px;
      display: inline-block;
      vertical-align: middle;
      object-fit: cover;
      margin-right: 10px;
      cursor: pointer;
    }
    .v-progress-circular {
      margin-right: 10px;
    }
    .v-icon {
      margin-left: 5px;
      padding: 4px;
    }
    span {
      cursor: default;
      white-space: nowrap;
      max-width: calc(100% - 80px);
      display: inline-block;
      vertical-align: middle;
    }
  }
  &-form {
    background-color: $bh-blue--4;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: auto;

    &-body {
      height: 100%;
      overflow: auto;
      display: flex;
      flex-direction: column;
      .test {
        flex-grow: 1;
        height: 100%;
        overflow: auto;
      }
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }
  &__selected {
    .main-user-table {
      tr td {
        cursor: pointer;
      }
    }
  }
}
</style>
